import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useIntl } from 'react-intl';
import TextTableFilter from '../../common/TextTableFilter';
import InputField from '../../products/components/InputField';
import { getBrands } from '../../products/cors/_requests';
import { IBrandModel, ISelectOptions } from '../../products/cors/_models';
import fileDownload from 'js-file-download';
import * as XLSX from 'xlsx';

import { getStockReport } from "../core/_requests";
import SharedData from "../../common/SharedData";
import { TutorialVideoLink } from "../../../tenant/TutorialVideoLink";
import { prepareFilters } from "../../orders/core/_requests";
import { StockItem } from './core/_models';
import '../stock-video.css';

const StockReportFilters: React.FC = () => {
    const [showVideoHighlight, setShowVideoHighlight] = useState(false);
    const { formatMessage } = useIntl();
    const [searchParams, setSearchParams] = useSearchParams();
    const { currentUser, language } = SharedData();

    const [filterName, setFilterName] = useState(searchParams.get('filter_name') || '');
    const [filterBarcode, setFilterBarcode] = useState(searchParams.get('filter_barcode') || '');
    const [filterManufacturerId, setFilterManufacturerId] = useState(searchParams.get('filter_manufacturer_id') || '');
    const [brandOptions, setBrandOptions] = useState<ISelectOptions[]>([]);
    const [isLoadingBrands, setIsLoadingBrands] = useState<boolean>(true);

    useEffect(() => {
        // Check if the stock_video cookie exists
        const hasSeenVideo = document.cookie.split(';').some(item => item.trim().startsWith('stock_video='));
        // Only show the highlight if the cookie doesn't exist
        setShowVideoHighlight(!hasSeenVideo);
    }, []);

    useEffect(() => {
        const fetchBrands = async () => {
            if (!currentUser?.token) {
                setIsLoadingBrands(false);
                return;
            }
            setIsLoadingBrands(true);
            try {
                const { data } = await getBrands(currentUser.token, language);
                const formattedOptions = data.map((item: IBrandModel) => ({
                    label: item.name,
                    value: String(item.manufacturer_id)
                }));

                setBrandOptions([
                    { label: formatMessage({ id: 'FILTER.ALL_BRANDS' }), value: '' },
                    ...formattedOptions
                ]);
            } catch (error) {
                console.error("Error fetching brands:", error);
                setBrandOptions([{ label: formatMessage({ id: 'FILTER.ALL_BRANDS' }), value: '' }]);
            } finally {
                setIsLoadingBrands(false);
            }
        };
        fetchBrands();
    }, [currentUser, language, formatMessage]);

 
    const handleTextFilterChange = (id: string, value: string) => {
    };

    const handleBrandChange = (e: { target: { id: string; value: string } }) => {
        const { value } = e.target;
        setFilterManufacturerId(value);
        if (value) {
            searchParams.set('filter_manufacturer_id', value);
        } else {
            searchParams.delete('filter_manufacturer_id');
        }
        searchParams.set('current_page', '1');
    };

    const filter = () => {
        if (filterName) searchParams.set('filter_name', filterName); else searchParams.delete('filter_name');
        if (filterBarcode) searchParams.set('filter_barcode', filterBarcode); else searchParams.delete('filter_barcode');
        if (filterManufacturerId) searchParams.set('filter_manufacturer_id', filterManufacturerId); else searchParams.delete('filter_manufacturer_id');
        searchParams.set('current_page', '1');

        setSearchParams(searchParams, { replace: true });
    };

    const clearFilters = () => {
        setFilterName('');
        setFilterBarcode('');
        setFilterManufacturerId('');
        const limit = searchParams.get('limit');
        const newParams: Record<string, string> = { current_page: '1' };
        if (limit) {
            newParams.limit = limit;
        }
        setSearchParams(newParams, { replace: true });
    };

    return (
        <div className='card-body border-bottom pb-10 mb-5'>
            <div className='row'>
                <TextTableFilter
                    id='filter_name'
                    label={formatMessage({ id: 'FILTER.NAME' })}
                    value={filterName}
                    setValue={setFilterName}
                    onChange={handleTextFilterChange}
                    type='text'
                />

                <TextTableFilter
                    id='filter_barcode'
                    label={formatMessage({ id: 'FILTER.BARCODE' })}
                    value={filterBarcode}
                    setValue={setFilterBarcode}
                    onChange={handleTextFilterChange}
                    type='text'
                />

                 <div className='col-sm-12 col-md-3 mb-3'>
                     <label className='form-label fw-bolder text-dark fs-6'>
                         {formatMessage({ id: 'FILTER.BRAND_ID' })}
                     </label>
                    <InputField
                        id='filter_manufacturer_id'
                        type='select'
                        options={brandOptions}
                        onChange={handleBrandChange}
                        value={filterManufacturerId}
                        isSearchable={true}
                    />
                 </div>
                <div className='col-sm-12 col-md-2'>
                    <button
                        className='btn btn-light mt-md-10 w-100 fs-6 fw-semibold'
                        onClick={filter}
                    >
                        {formatMessage({ id: "filter" })}
                    </button>
                </div>
                <div className='col-sm-12 col-md-2'>
                    <button
                        type='reset'
                        className='btn btn-light mt-md-10 w-100 fs-6 fw-semibold'
                        onClick={clearFilters}
                    >
                        {formatMessage({ id: "reset" })}
                    </button>
                </div>
                <div className='col-sm-12 col-md-2'>
                    <TutorialVideoLink
                        section="stock_reports"
                        fallback="https://www.youtube.com/watch?v=XwmsEpefSlU"
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`text-primary d-flex align-items-center mt-md-10 p-3 ${showVideoHighlight ? 'video-highlight' : ''}`}
                        style={{ direction: language === 'ar' ? 'rtl' : 'ltr' }}
                        onClick={() => {
                            document.cookie = "stock_video=seen";
                            setShowVideoHighlight(false);
                        }}
                    >
                        {language === 'ar' ? (
                            <>
                                <span>{formatMessage({ id: 'video_link' })}</span>
                                <i className='fab fa-youtube mx-2 text-danger fs-2'></i>
                            </>
                        ) : (
                            <>
                                <i className='fab fa-youtube mx-2 text-danger fs-2'></i>
                                <span>{formatMessage({ id: 'video_link' })}</span>
                            </>
                        )}
                    </TutorialVideoLink>
                </div>
            </div>
        </div>
    );
};

export default StockReportFilters;
